/**
 * Converts an array or a single value to a comma-separated string.
 */
export const arrayToString = (val: string | string[]): string => {
  val = Array.isArray(val) ? val : [val];
  return val.join(", ");
};

/**
 * Formats dataset information into a string.
 */
export const formatDataset = (
  samples: number,
  epochs: number,
  name: string | null | undefined
): string => {
  const perEpochSamples = epochs > 0 ? samples / epochs : samples;
  const namePrefix = name ? `${name} — ` : "";

  const terms: string[] = [
    namePrefix,
    String(perEpochSamples),
    epochs > 1 ? `x ${epochs} ` : "",
    samples === 1 ? "sample" : "samples",
  ];

  return terms.join(" ");
};

/**
 * Formats a duration given in seconds into a human-readable string.
 */
export const formatTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)} sec`;
  } else if (seconds < 60 * 60) {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return sec > 0 ? `${min} min ${sec} sec` : `${min} min`;
  } else if (seconds < 60 * 60 * 24) {
    const hours = Math.floor(seconds / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const sec = Math.floor(seconds % 60);
    const parts = [`${hours} hr`];
    if (minutes > 0) parts.push(`${minutes} min`);
    if (sec > 0) parts.push(`${sec} sec`);
    return parts.join(" ");
  } else {
    const days = Math.floor(seconds / (60 * 60 * 24));
    const hours = Math.floor((seconds % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const sec = Math.floor(seconds % 60);
    const parts = [`${days} days`];
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0) parts.push(`${minutes} min`);
    if (sec > 0) parts.push(`${sec} sec`);
    return parts.join(" ");
  }
};

/**
 * Formats a number to a string with specific decimal places for prettiness.
 */
export function formatPrettyDecimal(
  num: number,
  maxDecimals: number = 3
): string {
  const numDecimalPlaces = num.toString().includes(".")
    ? num.toString().split(".")[1]?.length || 0
    : 0;

  if (numDecimalPlaces === 0) {
    return num.toFixed(1);
  } else if (numDecimalPlaces > maxDecimals) {
    return num.toFixed(maxDecimals);
  } else {
    return num.toString();
  }
}

/**
 * Formats a number to a string without trailing zeroes after the decimal point.
 */
export function formatDecimalNoTrailingZeroes(num: number): string {
  // This isn't a number, continue
  // TODO: Remove this, its crazy
  if (typeof num !== "number") {
    return num;
  }

  if (num.toString().includes(".")) {
    const decimal = num.toString().split(".")[1];
    const trimmed = decimal?.replace(/\.?0+$/, "");
    return num.toFixed(trimmed?.length);
  } else {
    return num.toFixed(0);
  }
}

/**
 * Converts a string to title case.
 */
export function toTitleCase(str: string): string {
  if (!str) {
    return str;
  }

  return str
    .split(" ")
    .map((w) =>
      w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w
    )
    .join(" ");
}

/**
 * Formats a number to a string with no decimals
 */
export function formatNoDecimal(num: number): string {
  // This isn't a number, continue
  // TODO: remove This is crazy
  if (typeof num !== "number") {
    return num;
  }

  // Round to a whole number
  const rounded = Math.round(num);
  return rounded.toFixed(0);
}

/**
 * Formats a number to a string
 */
export function formatNumber(num: number): string {
  return num.toLocaleString(navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5,
  });
}

/**
 * Formats a number to a string without trailing zeroes after the decimal point.
 */
export function formatDateTime(date: Date): string {
  const options = {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };

  // Use the default system locale and timezone
  // @ts-expect-error options as passed is correct
  return new Intl.DateTimeFormat(undefined, options).format(date);
}

/**
 * Returns the formatted duration between two dates.
 */
export function formatDuration(start: Date, end: Date): string {
  const durationMs = end.getTime() - start.getTime();
  const durationSec = durationMs / 1000;
  return formatTime(durationSec);
}

/**
 * Compact duration format: "45s", "4m", "2h 5m".
 * Drops zero-valued trailing units and uses single-char suffixes.
 */
export function formatDurationShort(start: Date, end: Date): string {
  const totalSec = Math.round((end.getTime() - start.getTime()) / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const mins = Math.floor(totalSec / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (remMins === 0) return `${hours}h`;
  return `${hours}h ${remMins}m`;
}

/**
 * Truncates a string from the center, showing start and end with ellipsis in the middle.
 * @param str - The string to truncate
 * @param maxLength - Maximum length of the output string (default: 50)
 * @returns The truncated string with ellipsis in the middle if needed
 */
export function centerTruncate(str: string, maxLength: number = 50): string {
  if (str.length <= maxLength) {
    return str;
  }

  const ellipsis = " … ";
  const availableLength = maxLength - ellipsis.length;

  // If maxLength is too small to show anything meaningful
  if (availableLength <= 0) {
    return ellipsis;
  }

  // Calculate how much to show from start and end
  const startLength = Math.ceil(availableLength / 2);
  const endLength = Math.floor(availableLength / 2);

  const start = str.slice(0, startLength);
  const end = str.slice(-endLength);

  return start + ellipsis + end;
}

export function formatPercent(value: number, maxDecimals?: number): string {
  return `${formatPrettyDecimal(value * 100, maxDecimals ?? 2)}%`;
}

/**
 * Formats a duration in milliseconds as a seconds string with up to 3 decimal
 * places (e.g. "1.234 s"). Always renders in seconds — does not decompose into
 * minutes/hours; use formatTime for that.
 */
export function formatMs(ms: number): string {
  return `${(ms / 1000).toLocaleString(navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} s`;
}

/**
 * Formats a dollar amount: 2 significant digits below a dollar (per-call and
 * per-sample costs are often sub-cent, where 2 decimals would render "$0.00"),
 * 2 fraction digits with grouping at or above.
 */
export function formatCurrency(dollars: number): string {
  // 0.995+ rounds up to a dollar — send it to the fraction-digit branch so it
  // renders "$1.00", never bare "$1".
  if (dollars > 0 && dollars < 0.995) {
    return `$${dollars.toLocaleString(navigator.language, {
      // minimum too: significant-digit formatting drops trailing zeros,
      // and "$0.1" reads wrong for currency ("$0.10" doesn't).
      minimumSignificantDigits: 2,
      maximumSignificantDigits: 2,
    })}`;
  }
  return `$${dollars.toLocaleString(navigator.language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Formats a byte count as a human-readable string (B, KB, MB, GB, TB).
 */
export function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx++;
  }
  const formatted = value.toLocaleString(navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: unitIdx === 0 ? 0 : 2,
  });
  return `${formatted} ${units[unitIdx]}`;
}

/**
 * Stringifies a value for display or sorting, JSON-encoding objects and arrays
 * so they don't collapse to "[object Object]". Non-objects (including null and
 * undefined) match `String()` semantics.
 */
export function valueAsString(value: unknown): string {
  return value !== null && typeof value === "object"
    ? JSON.stringify(value)
    : String(value);
}
